import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChannelListItem } from "@cml/shared";
import { api } from "../lib/api";
import { getStartParam } from "../lib/telegram";
import { useAppStore } from "../store/app";

/** The channels the user belongs to (member ∪ admin) — powers the switcher. */
export function useChannels() {
  return useQuery<ChannelListItem[]>({
    queryKey: ["channels"],
    queryFn: async () => (await api<{ channels: ChannelListItem[] }>("/channels")).channels,
  });
}

/**
 * Resolve the active channel object. Falls back to the first channel when the
 * persisted `activeChannelId` is unset or stale (e.g. the user left it), and
 * writes that choice back so the rest of the app has a stable scope.
 */
export function useActiveChannel(): {
  channel: ChannelListItem | null;
  channels: ChannelListItem[];
  isLoading: boolean;
} {
  const { data: channels = [], isLoading } = useChannels();
  const activeChannelId = useAppStore((s) => s.activeChannelId);
  const setActiveChannel = useAppStore((s) => s.setActiveChannel);

  const resolved =
    channels.find((c) => c.id === activeChannelId) ?? channels[0] ?? null;

  useEffect(() => {
    if (resolved && resolved.id !== activeChannelId) setActiveChannel(resolved.id);
  }, [resolved, activeChannelId, setActiveChannel]);

  return { channel: resolved, channels, isLoading };
}

/**
 * Enrollment-on-open: when the TMA is launched via a channel deep link
 * (`?startapp=channel_<id>`), join that channel so a non-admin user immediately
 * has it scoped — the launch-context step that makes the app useful to ordinary
 * users, not just admins. Runs once on mount.
 *
 * The API gates enrollment (public channels free; private ones verify real
 * Telegram membership), so a denied join just falls through silently — the user
 * still sees whatever channels they already belong to.
 */
export function useEnrollFromStartParam(): void {
  const qc = useQueryClient();
  const setActiveChannel = useAppStore((s) => s.setActiveChannel);

  useEffect(() => {
    const sp = getStartParam();
    if (!sp || !sp.startsWith("channel_")) return;
    const channelId = sp.slice("channel_".length);

    let cancelled = false;
    void (async () => {
      try {
        const { channel } = await api<{ channel: ChannelListItem }>(
          `/channels/${channelId}/enroll`,
          { method: "POST" },
        );
        if (cancelled) return;
        setActiveChannel(channel.id);
        await qc.invalidateQueries({ queryKey: ["channels"] });
      } catch {
        // Not enrollable (e.g. private channel, caller isn't a Telegram member).
        // Silent — the app shows the user's existing channels / empty state.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [qc, setActiveChannel]);
}
